import React from 'react';
import { motion } from 'framer-motion';

const Section = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, ease: 'easeOut', delay }}
    className="max-w-3xl mx-auto"
  >
    {children}
  </motion.div>
);

const Divider = () => (
  <div className="h-px w-full bg-gray-200 my-10" />
);

const CardSection = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay }}
    className="w-full rounded-3xl border border-gray-200 bg-white shadow-sm p-8 lg:p-10 mb-6"
  >
    {children}
  </motion.div>
);

const PrivacyPolicy = () => {
  return (
    <main className="min-h-screen bg-white text-gray-900">
      {/* Prominent Hero Section */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 pointer-events-none">
          {/* Mesh-like motion graphic (from main hero style), made more visible */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ duration: 1 }}
            className="absolute -top-24 left-1/2 -translate-x-1/2 w-[900px] h-[900px]"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
              className="w-full h-full"
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                {/* Outer nodes */}
                <motion.circle cx="20" cy="20" r="1.6" fill="#3b82f6" opacity="0.85" />
                <motion.circle cx="80" cy="20" r="1.6" fill="#10b981" opacity="0.85" />
                <motion.circle cx="20" cy="80" r="1.6" fill="#f59e0b" opacity="0.85" />
                <motion.circle cx="80" cy="80" r="1.6" fill="#ef4444" opacity="0.85" />

                {/* Middle nodes */}
                <motion.circle cx="35" cy="35" r="1.2" fill="#3b82f6" opacity="0.8" />
                <motion.circle cx="65" cy="35" r="1.2" fill="#10b981" opacity="0.8" />
                <motion.circle cx="35" cy="65" r="1.2" fill="#f59e0b" opacity="0.8" />
                <motion.circle cx="65" cy="65" r="1.2" fill="#ef4444" opacity="0.8" />

                {/* Mesh connections */}
                <motion.line x1="20" y1="20" x2="80" y2="20" stroke="#60a5fa" strokeWidth="0.25" opacity="0.35"
                  animate={{ opacity: [0.2, 0.6, 0.2] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }} />
                <motion.line x1="20" y1="80" x2="80" y2="80" stroke="#fbbf24" strokeWidth="0.25" opacity="0.35"
                  animate={{ opacity: [0.2, 0.6, 0.2] }} transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }} />
                <motion.line x1="20" y1="20" x2="20" y2="80" stroke="#a78bfa" strokeWidth="0.25" opacity="0.35"
                  animate={{ opacity: [0.2, 0.6, 0.2] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }} />
                <motion.line x1="80" y1="20" x2="80" y2="80" stroke="#34d399" strokeWidth="0.25" opacity="0.35"
                  animate={{ opacity: [0.2, 0.6, 0.2] }} transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }} />

                {/* Inner connections */}
                <motion.line x1="35" y1="35" x2="65" y2="35" stroke="#60a5fa" strokeWidth="0.2" opacity="0.4"
                  animate={{ opacity: [0.15, 0.55, 0.15] }} transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }} />
                <motion.line x1="35" y1="65" x2="65" y2="65" stroke="#f87171" strokeWidth="0.2" opacity="0.4"
                  animate={{ opacity: [0.15, 0.55, 0.15] }} transition={{ duration: 1.9, repeat: Infinity, ease: 'easeInOut' }} />
                <motion.line x1="35" y1="35" x2="35" y2="65" stroke="#34d399" strokeWidth="0.2" opacity="0.35"
                  animate={{ opacity: [0.15, 0.5, 0.15] }} transition={{ duration: 1.7, repeat: Infinity, ease: 'easeInOut' }} />
                <motion.line x1="65" y1="35" x2="65" y2="65" stroke="#f59e0b" strokeWidth="0.2" opacity="0.35"
                  animate={{ opacity: [0.15, 0.5, 0.15] }} transition={{ duration: 2.1, repeat: Infinity, ease: 'easeInOut' }} />

                {/* Cross connections */}
                <motion.line x1="20" y1="20" x2="65" y2="65" stroke="#93c5fd" strokeWidth="0.15" opacity="0.25"
                  animate={{ opacity: [0.08, 0.3, 0.08] }} transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }} />
                <motion.line x1="80" y1="20" x2="35" y2="65" stroke="#6ee7b7" strokeWidth="0.15" opacity="0.25"
                  animate={{ opacity: [0.08, 0.3, 0.08] }} transition={{ duration: 3.0, repeat: Infinity, ease: 'easeInOut' }} />
                <motion.line x1="20" y1="80" x2="65" y2="35" stroke="#fde68a" strokeWidth="0.15" opacity="0.25"
                  animate={{ opacity: [0.08, 0.3, 0.08] }} transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }} />
                <motion.line x1="80" y1="80" x2="35" y2="35" stroke="#fca5a5" strokeWidth="0.15" opacity="0.25"
                  animate={{ opacity: [0.08, 0.3, 0.08] }} transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }} />
              </svg>
            </motion.div>
          </motion.div>
        </div>
        <div className="container-custom py-24 lg:py-32 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-6xl font-bold font-slim tracking-tight">Privacy Policy — FlowMatic</h1>
            <p className="mt-4 text-lg text-gray-600">Last Updated: October 2025</p>
            <div className="mt-10 flex justify-center">
              <svg width="160" height="160" viewBox="0 0 100 100" className="opacity-80">
                <defs>
                  <linearGradient id="pp-grad" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#8b5cf6" />
                  </linearGradient>
                </defs>
                <circle cx="50" cy="50" r="40" fill="none" stroke="url(#pp-grad)" strokeWidth="1.5" opacity="0.35">
                  <animate attributeName="r" values="36;40;36" dur="4s" repeatCount="indefinite" />
                </circle>
                <circle cx="50" cy="50" r="26" fill="none" stroke="url(#pp-grad)" strokeWidth="1.2" opacity="0.45">
                  <animate attributeName="r" values="22;26;22" dur="3.5s" repeatCount="indefinite" />
                </circle>
                <circle cx="50" cy="50" r="2.5" fill="url(#pp-grad)">
                  <animate attributeName="r" values="2;3;2" dur="2s" repeatCount="indefinite" />
                </circle>
              </svg>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="container-custom py-16 lg:py-20">

        {/* Summary Cards */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Personal Information',
                desc: 'Details you share like name, company, email, and billing.',
              },
              {
                title: 'Usage Data',
                desc: 'Technical data like IP, device, browser, and page interactions.',
              },
              {
                title: 'Client Data',
                desc: 'Workflow and integration data used to deliver services.',
              },
              {
                title: 'How We Use It',
                desc: 'Provide and improve products, support, security, and accounts.',
              },
              {
                title: 'Your Rights',
                desc: 'Access, delete, correct data, and withdraw consent.',
              },
              {
                title: 'Security & Cookies',
                desc: 'Industry-standard safeguards and cookie-based improvements.',
              },
            ].map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.45, delay: i * 0.05, ease: 'easeOut' }}
                className="relative rounded-2xl p-6 bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="absolute -top-3 -right-3 w-16 h-16 opacity-20">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <defs>
                      <linearGradient id={`card-grad-${i}`} x1="0" x2="1" y1="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#8b5cf6" />
                      </linearGradient>
                    </defs>
                    <circle cx="50" cy="50" r="28" fill="none" stroke={`url(#card-grad-${i})`} strokeWidth="4">
                      <animate attributeName="r" values="24;28;24" dur="4s" repeatCount="indefinite" />
                    </circle>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
                <p className="text-gray-600 leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <Section>
          <p className="text-lg leading-relaxed text-gray-700">
            At FlowMatic, your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you visit our website or use our services.
          </p>
        </Section>

        <Divider />

        <CardSection delay={0.05}>
          <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
            <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">
              Information We Collect
            </span>
          </h2>
          <p className="text-gray-700 mb-4">We may collect the following types of information:</p>
          <h3 className="text-xl font-semibold mb-2">a. Personal Information</h3>
          <ul className="list-disc pl-6 space-y-1 text-gray-700">
            <li>Name</li>
            <li>Company name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Billing and payment details</li>
          </ul>
          <h3 className="text-xl font-semibold mt-6 mb-2">b. Usage Data</h3>
          <ul className="list-disc pl-6 space-y-1 text-gray-700">
            <li>IP address</li>
            <li>Browser type and version</li>
            <li>Device information</li>
            <li>Pages visited and time spent on the website</li>
          </ul>
          <h3 className="text-xl font-semibold mt-6 mb-2">c. Client Data</h3>
          <p className="text-gray-700">
            If you use our AI agents or automation tools, we may collect operational data related to workflow details you provide and data shared through integrations (e.g., CRMs, chat tools, or form inputs). We process this data only to deliver and improve your automation services.
          </p>
        </CardSection>

        <CardSection delay={0.1}>
          <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
            <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">
              How We Use Your Information
            </span>
          </h2>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Provide, maintain, and improve our AI automation products</li>
            <li>Customize AI agents for your company’s needs</li>
            <li>Communicate with you regarding updates, support, and offers</li>
            <li>Process transactions and manage client accounts</li>
            <li>Ensure security and prevent fraud</li>
          </ul>
          <p className="mt-4 text-gray-700">We do not sell or rent your personal data to third parties.</p>
        </CardSection>

        <CardSection delay={0.15}>
          <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
            <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">
              Data Sharing and Disclosure
            </span>
          </h2>
          <p className="text-gray-700 mb-3">We may share data only in the following situations:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>With service providers who help operate our business (e.g., hosting, analytics, payment gateways)</li>
            <li>For legal reasons, if required by law or governmental authorities</li>
            <li>With your consent, for integrations or third-party connections you authorize</li>
          </ul>
          <p className="mt-3 text-gray-700">All partners are required to maintain strict confidentiality.</p>
        </CardSection>

        <CardSection delay={0.2}>
          <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
            <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">
              Data Security
            </span>
          </h2>
          <p className="text-gray-700">
            We implement industry-standard measures (encryption, secure servers, limited access) to protect your data. However, no system is 100% secure — we encourage clients to use secure networks and credentials when interacting with our systems.
          </p>
        </CardSection>

        <CardSection delay={0.25}>
          <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
            <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">
              Your Rights
            </span>
          </h2>
          <p className="text-gray-700 mb-3">Depending on your location, you may have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Access or request deletion of your personal data</li>
            <li>Request correction of inaccurate information</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p className="mt-4 text-gray-700">To exercise these rights, contact us at:</p>
          <p className="font-medium">support@flowmatic.com</p>
        </CardSection>

        <CardSection delay={0.3}>
          <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
            <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">
              Cookies and Tracking
            </span>
          </h2>
          <p className="text-gray-700">
            Our website uses cookies to enhance your browsing experience and analyze site traffic. You can choose to disable cookies in your browser settings, though some parts of the website may not function properly.
          </p>
        </CardSection>

        <CardSection delay={0.35}>
          <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
            <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">
              Retention of Data
            </span>
          </h2>
          <p className="text-gray-700">
            We retain client data only for as long as needed to provide services or comply with legal obligations. When no longer required, data is securely deleted.
          </p>
        </CardSection>

        <CardSection delay={0.4}>
          <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
            <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">
              Changes to This Policy
            </span>
          </h2>
          <p className="text-gray-700">
            We may update this Privacy Policy from time to time. The updated version will always include the “Last Updated” date at the top.
          </p>
        </CardSection>

        <CardSection delay={0.45}>
          <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
            <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">
              Contact Us
            </span>
          </h2>
          <p className="text-gray-700">If you have any questions about this Privacy Policy or how your data is handled, contact us at:</p>
          <p className="mt-2 font-medium">support@flowmatic.com</p>
          <a href="https://www.flowmatic.com" target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-blue-600 hover:text-purple-600 transition-colors">www.flowmatic.com</a>
        </CardSection>
      </section>
    </main>
  );
};

export default PrivacyPolicy;


